/**
 * Owner: Masa Bayu
 * File: design-token.ts
 * Date:Time: 2026-09-13 21:34
 * Description: 22E design token groups and opaque path references.
 */
export const QX_DESIGN_TOKEN_GROUPS = [
    'color',
    'typography',
    'spacing',
    'shape',
    'elevation',
    'border',
];
const FORBIDDEN = [
    'css',
    'var',
    'value',
    'hex',
    'px',
    'rem',
    'em',
    'boxShadow',
    'className',
    'style',
    'dom',
    'react',
    'component',
];
function assertNoForbidden(record, label) {
    for (const key of FORBIDDEN) {
        if (key in record) {
            throw new Error(`${label} must not include render field "${key}"`);
        }
    }
}
function assertName(value, label) {
    if (typeof value !== 'string' || value.trim().length === 0 || /\s/.test(value)) {
        throw new Error(`${label} must be a non-empty id without whitespace`);
    }
    if (value.includes('.')) {
        throw new Error(`${label} must not include '.' (use group + name separately)`);
    }
}
export function isQXDesignTokenGroup(value) {
    return typeof value === 'string' && QX_DESIGN_TOKEN_GROUPS.includes(value);
}
export function createQXDesignTokenReference(group, name) {
    assertNoForbidden({ group, name }, 'Design token reference');
    if (!isQXDesignTokenGroup(group)) {
        throw new Error(`Invalid design token group: ${String(group)}`);
    }
    assertName(name, 'Design token name');
    return Object.freeze({ group, name: name.trim() });
}
export function formatQXDesignTokenPath(reference) {
    return `${reference.group}.${reference.name}`;
}
export function parseQXDesignTokenPath(path) {
    if (typeof path !== 'string' || path.trim().length === 0) {
        throw new Error('Design token path must be a non-empty string');
    }
    const trimmed = path.trim();
    const dot = trimmed.indexOf('.');
    if (dot <= 0 || dot !== trimmed.lastIndexOf('.') || dot === trimmed.length - 1) {
        throw new Error(`Invalid design token path "${path}" (expected group.name)`);
    }
    const group = trimmed.slice(0, dot);
    const name = trimmed.slice(dot + 1);
    if (!isQXDesignTokenGroup(group)) {
        throw new Error(`Invalid design token group in path: ${group}`);
    }
    return createQXDesignTokenReference(group, name);
}
export function isQXDesignTokenReference(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    return isQXDesignTokenGroup(record.group) && typeof record.name === 'string';
}
export function designTokenReferenceKey(reference) {
    return formatQXDesignTokenPath(reference);
}
//# sourceMappingURL=design-token.js.map